/**
 * Materialized Tasks Hooks (require ActiveBoardContext)
 * Subscribes to both globalTasksStore and boardTaskViewStore
 */

import { useMemo, useCallback, useRef, useSyncExternalStore } from 'react';
import { materializeTasks } from '../stores/roadmapStore';
import { globalTasksStore } from '../../stores/globalTasksStore';
import { getBoardTaskViewStore } from '../stores/boardTaskViewStore';
import { useActiveBoardContext } from '../stores/ActiveBoardContext';
import type { Task } from '../../stores/types';

export function useMaterializedTasks(): Task[] {
  const { boardId } = useActiveBoardContext();
  const viewStore = getBoardTaskViewStore(boardId);
  const cachedRef = useRef<Task[]>([]);

  const subscribe = useCallback((onChange: () => void) => {
    const unsub1 = globalTasksStore.subscribe(onChange);
    const unsub2 = viewStore.subscribe(onChange);
    return () => {
      unsub1.unsubscribe();
      unsub2.unsubscribe();
    };
  }, [viewStore]);

  const getSnapshot = useCallback(() => {
    const newTasks = materializeTasks(boardId);

    if (JSON.stringify(cachedRef.current) === JSON.stringify(newTasks)) {
      return cachedRef.current;
    }
    cachedRef.current = newTasks;
    return newTasks;
  }, [boardId]);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function useTask(taskId: string): Task | undefined {
  const tasks = useMaterializedTasks();
  return useMemo(() => tasks.find(t => t.id === taskId), [tasks, taskId]);
}

export const useAllTasks = useMaterializedTasks;

export function useColumnTasks(column: string): Task[] {
  const allTasks = useMaterializedTasks();
  const cachedRef = useRef<Task[]>([]);

  return useMemo(() => {
    const newTasks = allTasks.filter(task => task.column === column);

    if (JSON.stringify(cachedRef.current) === JSON.stringify(newTasks)) {
      return cachedRef.current;
    }
    cachedRef.current = newTasks;
    return newTasks;
  }, [allTasks, column]);
}
